/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import { getUserPermissionForCurrentApp } from "./api/TriAuthApi";

class TriAuthChecker {
  constructor(userPermission) {
    this.userPermission = userPermission;
  }

  hasMinimumAppPermission() {
    return this.hasLicense() && this.hasReadPermission();
  }

  hasReadPermission() {
    return this.hasPermission() && this.userPermission.canRead;
  }

  hasCreatePermission() {
    return this.hasPermission() && this.userPermission.canCreate;
  }

  hasDeletePermission() {
    return this.hasPermission() && this.userPermission.canDelete;
  }

  hasUpdatePermission() {
    return this.hasPermission() && this.userPermission.canUpdate;
  }

  hasActionPermission(actionName, actionGroup, dataSourcesPath) {
    if (!this.hasPermission()) return false;
    const { actions } = this.userPermission;
    let action = null;
    if (actions != null) {
      action = actions.find(
        (currentAction) =>
          currentAction.actionName === actionName &&
          currentAction.actionGroup === actionGroup &&
          currentAction.dataSourcesPath === dataSourcesPath
      );
    }

    if (action != null) {
      return action.permission;
    } else {
      return false;
    }
  }

  hasPermission() {
    return this.userPermission != null;
  }

  hasLicense() {
    return this.hasPermission() && this.userPermission.hasLicense;
  }
}

export async function getAuthCheckerForCurrentApp() {
  let userPermission;
  try {
    userPermission = await getUserPermissionForCurrentApp();
  } catch (error) {
    userPermission = null;
  }
  return new TriAuthChecker(userPermission);
}
